import {ArrayUtils} from '@colonist/utils'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {UIGameManager} from '../UIGameManager'
import {UIGameMechanic} from '../UIGameEventController'
import {UIGamePopupCardConfirmation} from '../ui_game_popup/UIGamePopupCardConfirmation'
import {CardPopupInformation, CardDataController} from '@colonist/game-card-types'
import {UIGameMapDiceProbabilityView} from './UIGameMapDiceProbabilityView'
import {Sound} from '../../Sound'
import {ActionState, CardEnum} from '@colonist/game-states'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums

export abstract class UIGameInventorHighlightDiceController implements UIGameMechanic {
    protected readonly uiGameManager: UIGameManager
    private diceProbabilityViewsBeingHighlighted: UIGameMapDiceProbabilityView[]
    private selectedDiceNumbers: UIGameMapDiceProbabilityView[]
    private confirmationPopup: UIGamePopupCardConfirmation | undefined

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.diceProbabilityViewsBeingHighlighted = []
        this.selectedDiceNumbers = []
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.HighlightDiceNumbersForInventor: this.highlightDiceNumbers(data); break
            case UIGameEventEnums.GameStateUpdated: this.gameStateUpdated(); break
            default: break
        }
    }

    private highlightDiceNumbers(highlightedIndexes: number[]) {
        const isAlreadyHighlighted = this.diceProbabilityViewsBeingHighlighted.length != 0
        if(isAlreadyHighlighted) return

        for(const highlightIndex of highlightedIndexes) {
            const tileView = this.uiGameManager.mapController.tileViews[highlightIndex]
            if(tileView.probabilityView == undefined) continue

            tileView.probabilityView.highlight()
            this.diceProbabilityViewsBeingHighlighted.push(tileView.probabilityView)
        }
        this.startAnimation()
        this.showInventorConfirmationPopup()
    }

    private startAnimation() {
        this.animateDiceProbabilityViewsIfUnselected()
        this.setClickAction()
    }

    private animateDiceProbabilityViewsIfUnselected() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            const diceProbabilityViewIsSelected = ArrayUtils.elementInArray(this.selectedDiceNumbers, diceProbabilityView)
            if(diceProbabilityViewIsSelected) continue

            diceProbabilityView.resetAnimation()
            diceProbabilityView.animate()
        }
    }

    private setClickAction() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.eventMode = 'static'
            diceProbabilityView.on('pointerdown', () => {
                this.clickedDiceNumber(diceProbabilityView)
            })
        }
    }

    private endAnimation() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) diceProbabilityView.resetAnimation()
        this.removeClickAction()
    }

    private removeClickAction() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.removeAllListeners('pointerdown')
        }
    }

    private clickedDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        Sound.playSound(Sound.SoundType.Click)
        const alreadySelectedDiceView = ArrayUtils.elementInArray(this.selectedDiceNumbers, diceProbabilityView)
        alreadySelectedDiceView ? this.deselectDiceNumber(diceProbabilityView) : this.selectDiceNumber(diceProbabilityView)
    }

    private selectDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        const alreadySelectedTwoNumbers = this.selectedDiceNumbers.length == 2
        if(alreadySelectedTwoNumbers) return

        this.selectedDiceNumbers.push(diceProbabilityView)
        diceProbabilityView.removeHighlight()
        diceProbabilityView.resetAnimation()
        this.updateCheckButtonOpacity()
    }

    private deselectDiceNumber(diceProbabilityView: UIGameMapDiceProbabilityView) {
        ArrayUtils.removeElementFromArray(this.selectedDiceNumbers, diceProbabilityView)
        this.animateDiceProbabilityViewsIfUnselected()
        diceProbabilityView.highlight()
        this.updateCheckButtonOpacity()
    }

    private updateCheckButtonOpacity() {
        if(this.confirmationPopup == undefined) return
        this.confirmationPopup.checkButtonInactiveOpacity()
        const amountOfSelectDiceNumbers = this.selectedDiceNumbers.length
        if(amountOfSelectDiceNumbers != 2) return
        this.confirmationPopup.checkButtonActiveOpactiy()
    }

    private showInventorConfirmationPopup() {
        const isPopupAlreadyShown = this.confirmationPopup != undefined
        if(isPopupAlreadyShown) return

        const cardData = CardDataController.getCardDataForCard(CardEnum.ProgressCardScienceInventor)
        if(cardData == undefined) return
        const cardConfirmationPopupFormat: CardPopupInformation = {
            cardEnum: cardData.cardEnum,
            cardBody: cardData.popupBody,
        }
        this.confirmationPopup = this.createInventorPopup(cardConfirmationPopupFormat)
        this.confirmationPopup.checkButtonInactiveOpacity()

        const checkAction = () => {
            this.checkAction()
        }
        this.confirmationPopup.setCheckAction(checkAction)

        const cancelAction = () => {
            this.cancelAction()
        }
        this.confirmationPopup.setCancelAction(cancelAction)
        this.uiGameManager.eventController.sendEvent(this.confirmationPopup, GameStateEnums.UIGameEventEnums.PopupActive)
    }

    protected abstract createInventorPopup(cardConfirmationPopupFormat: CardPopupInformation): UIGamePopupCardConfirmation

    private checkAction() {
        const alreadySelectedTwoNumbers = this.selectedDiceNumbers.length == 2
        if(!alreadySelectedTwoNumbers) return

        const selectedTiles = this.getSelectedTileIndices()

        this.uiGameManager.socketGameSend.selectedTiles(selectedTiles)
        this.closeUI()
    }

    private getSelectedTileIndices(): number[] {
        const selectedTileIndexs: number[] = []
        for(const selectedDiceNumber of this.selectedDiceNumbers) {
            const tileIndex = this.uiGameManager.gameState.mapState.tileState.getTileHexIndex(selectedDiceNumber.hexFace)
            if(tileIndex == undefined) continue

            selectedTileIndexs.push(tileIndex)
        }
        return selectedTileIndexs
    }

    private cancelAction() {
        this.uiGameManager.socketGameSend.cancelAction()
        this.closeUI()
    }

    private closeUI() {
        if(this.confirmationPopup == undefined) return
        if(this.confirmationPopup.parent != undefined) this.confirmationPopup.parent.removeChild(this.confirmationPopup)
        this.confirmationPopup = undefined
        this.removeHighlights()
        this.endAnimation()
        this.selectedDiceNumbers = []
        this.diceProbabilityViewsBeingHighlighted = []
        this.uiGameManager.eventController.sendEvent(undefined, GameStateEnums.UIGameEventEnums.PopupActive)
    }

    private removeHighlights() {
        for(const diceProbabilityView of this.diceProbabilityViewsBeingHighlighted) {
            diceProbabilityView.removeHighlight()
        }
    }

    private gameStateUpdated() {
        if(this.uiGameManager.gameController.currentState.actionState == ActionState.SelectTileProductionNumbersToSwap) return
        this.closeUI()
    }
}
